import { UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import APIError from 'types/api_error';
import { DashboardTemplate } from 'types/dashboard_template';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { sanitizeQueryKey } from 'services/query_key';

type UseDashboardTemplateOptions = UseQueryOptions<
  DashboardTemplate,
  APIError
> & {
  templateId: string | undefined;
};

export function useDashboardTemplate({
  templateId,
  queryKey,
  ...options
}: UseDashboardTemplateOptions) {
  const { applicationID, client } = useAPI();

  return useAuthenticatedQuery(
    queryKey ?? getDashboardTemplateQueryKey(applicationID, templateId),
    () => readDashboardTemplate(client, applicationID, templateId),
    {
      ...options,
      enabled: Boolean(templateId) && (options.enabled ?? true),
    }
  );
}

export function useReadDashboardTemplate() {
  const { applicationID, client } = useAPI();
  const queryClient = useQueryClient();

  return (templateId: DashboardTemplate['id']) =>
    queryClient.fetchQuery<DashboardTemplate, APIError>({
      queryKey: sanitizeQueryKey(
        getDashboardTemplateQueryKey(applicationID, templateId)
      ),
      queryFn: () => readDashboardTemplate(client, applicationID, templateId),
    });
}

function getDashboardTemplateQueryKey(
  applicationID: string | undefined,
  templateId: string | undefined
) {
  return ['dashboard-template', applicationID, templateId];
}

export async function readDashboardTemplate(
  client: AxiosInstance,
  applicationID: string | undefined,
  templateId: string | undefined
) {
  if (!applicationID || !templateId) {
    throw {
      code: 'missing-route-parameters',
      status: 400,
      message: 'Missing dashboard template route parameters',
    } satisfies APIError;
  }

  const { data } = await client.get<DashboardTemplate>(
    `/internal/apps/${applicationID}/dashboard_templates/${templateId}`
  );

  return data;
}
